// Moteur de reconnaissance embarqué (mode hors ligne) : même calcul que
// backend/scan_engine.py, sur les fichiers du « pack scanner » téléchargé
// (modèles ONNX + index). onnxruntime-web est chargé à la demande depuis
// public/ort/ (hors du bundle webpack) ; son moteur .wasm fait partie du pack.
import { imageToRGB, letterbox, cropRGB, warpCard, rotate180, phash128, toTensorData } from "./imageOps";
import { quadsFromProb, guideQuad, refineBox, pickRefined } from "./detect";

const MEAN = { dino: [0.485, 0.456, 0.406], clip: [0.4815, 0.4578, 0.4082] };
const STD = { dino: [0.229, 0.224, 0.225], clip: [0.2686, 0.2613, 0.2758] };
const POP = new Uint8Array(256).map((_, i) => { let c = 0, v = i; while (v) { c += v & 1; v >>= 1; } return c; });

let ortPromise = null;
function loadOrt() {
  if (!ortPromise) {
    const url = `${window.location.origin}${process.env.PUBLIC_URL || ""}/ort/ort.wasm.bundle.min.mjs`;
    ortPromise = import(/* webpackIgnore: true */ url);
  }
  return ortPromise;
}

async function fetchBuffer(url) {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`Fichier du scanner illisible (${r.status})`);
  return r.arrayBuffer();
}

export class LocalEngine {
  /** urlOf(name) : URL lisible d'un fichier du pack (local sur l'appli). */
  constructor(manifest, urlOf) {
    this.m = manifest; this.urlOf = urlOf; this.sess = {};
  }

  async init(onStep) {
    const ort = await loadOrt();
    this.ort = ort;
    ort.env.wasm.numThreads = 1; // pas de SharedArrayBuffer dans la WebView
    ort.env.wasm.wasmPaths = { wasm: this.urlOf(this.m.runtime.file) };
    const f = this.m.files, n = this.m.count;
    onStep?.("index");
    const [cards, ph, dino, clip] = await Promise.all([
      fetch(this.urlOf(f.cards)).then((r) => r.json()),
      fetchBuffer(this.urlOf(f.phash)), fetchBuffer(this.urlOf(f.dino)), fetchBuffer(this.urlOf(f.clip)),
    ]);
    this.cards = cards.cards; this.sets = cards.sets;
    this.ph = new Uint8Array(ph);
    const vec = (buf, d) => ({ sc: new Float32Array(buf, 0, n), q: new Int8Array(buf, n * 4, n * d), d });
    this.vec = { dino: vec(dino, this.m.dims.dino), clip: vec(clip, this.m.dims.clip) };
    for (const k of ["det", "dino", "clip"]) {
      if (!this.m.models[k]) continue; // sans détecteur : repli sur le centre de la photo
      onStep?.(k);
      const buf = await fetchBuffer(this.urlOf(this.m.models[k].file));
      this.sess[k] = await ort.InferenceSession.create(buf, { executionProviders: ["wasm"], graphOptimizationLevel: "all" });
    }
  }

  async detect(img) {
    const S = this.m.det_size || 384;
    const { img: lb, k, ox, oy } = letterbox(img, S);
    const x = new Float32Array(3 * S * S);
    for (let i = 0; i < S * S; i++) for (let c = 0; c < 3; c++) x[c * S * S + i] = lb.data[i * 3 + c];
    const s = this.sess.det;
    const out = await s.run({ [s.inputNames[0]]: new this.ort.Tensor("float32", x, [1, 3, S, S]) });
    const t = out[s.outputNames[0]];
    const [, , h, w] = t.dims;
    return quadsFromProb(t.data, h, w, S).map((d) => ({
      quad: d.quad.map(([qx, qy]) => [(qx - ox) / k, (qy - oy) / k]), score: d.score,
    }));
  }

  // 2ᵉ passe : coins plus précis pour les petites cartes (photo de plusieurs cartes).
  async refine(rgb, quad) {
    const box = refineBox(quad, rgb.width, rgb.height);
    if (!box) return quad;
    const found = await this.detect(cropRGB(rgb, box));
    return pickRefined(quad, found.map((f) => f.quad.map(([x, y]) => [x + box[0], y + box[1]])));
  }

  async embed(name, card) {
    const s = this.sess[name];
    const x = new this.ort.Tensor("float32", toTensorData(card, MEAN[name], STD[name]), [1, 3, 224, 224]);
    const t = (await s.run({ [s.inputNames[0]]: x }))[s.outputNames[0]];
    const D = t.dims[t.dims.length - 1];
    const v = Float32Array.from(t.data.subarray(0, D)); // jeton CLS (DINO) ou image_embeds (CLIP)
    let nn = 0; for (const a of v) nn += a * a; nn = Math.sqrt(nn);
    return v.map((a) => a / nn);
  }

  // Score de chaque carte de l'index pour une rotation donnée.
  async rawScores(card) {
    const n = this.m.count, ph = phash128(card);
    const hp = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      let h = 0; for (let b = 0; b < 16; b++) h += POP[this.ph[i * 16 + b] ^ ph[b]];
      hp[i] = -h;
    }
    const S = { phash: hp };
    for (const k of ["dino", "clip"]) {
      const e = await this.embed(k, card), { sc, q, d } = this.vec[k], out = new Float32Array(n);
      for (let i = 0; i < n; i++) { let s = 0; const o = i * d; for (let j = 0; j < d; j++) s += q[o + j] * e[j]; out[i] = s * sc[i]; }
      S[k] = out;
    }
    return S;
  }

  async fused(card) {
    const a = await this.rawScores(card), b = await this.rawScores(rotate180(card));
    const n = this.m.count, w = this.m.weights, total = new Float32Array(n);
    for (const k of ["phash", "dino", "clip"]) {
      const s = new Float32Array(n); let mean = 0;
      for (let i = 0; i < n; i++) { s[i] = Math.max(a[k][i], b[k][i]); mean += s[i]; }
      mean /= n; let v = 0; for (let i = 0; i < n; i++) v += (s[i] - mean) ** 2;
      const sd = Math.sqrt(v / n) + 1e-9;
      for (let i = 0; i < n; i++) total[i] += (w[k] * (s[i] - mean)) / sd;
    }
    return total;
  }

  candidates(fused, k = 24) {
    const idx = Array.from(fused.keys()).sort((a, b) => fused[b] - fused[a]).slice(0, k);
    const { sc, q, d } = this.vec.dino, t = idx[0];
    const sim = (i) => { let s = 0; for (let j = 0; j < d; j++) s += q[i * d + j] * q[t * d + j]; return s * sc[i] * sc[t]; };
    const norm = (s) => (s || "").toLowerCase().normalize("NFKD").replace(/\p{M}/gu, "").trim();
    const first = norm(this.cards[t][1]);
    return idx.map((i, rank) => {
      const [id, name, localId, set_id] = this.cards[i];
      return { id, name, localId, set_id, set_name: this.sets[set_id], score: Math.round(fused[i] * 1000) / 1000,
        same_art: rank > 0 && norm(name) === first && sim(i) >= (this.m.same_art || 0.75) };
    });
  }

  /** img : HTMLImageElement / ImageBitmap. Même format de réponse que /scan/recognize. */
  async recognize(img, { detect = true } = {}) {
    const rgb = imageToRGB(img, 1600);
    const { width: w, height: h } = rgb;
    let found = detect && this.sess.det ? await this.detect(rgb) : [];
    for (const f of found) f.quad = await this.refine(rgb, f.quad);
    const fallback = !found.length;
    if (fallback) found = [{ quad: guideQuad(w, h), score: 0 }];
    const cards = [];
    for (const f of found) {
      const cand = this.candidates(await this.fused(warpCard(rgb, f.quad)));
      const other = cand.slice(1).find((c) => !c.same_art);
      const margin = cand[0].score - (other ? other.score : cand[0].score);
      cards.push({
        quad: f.quad.map(([x, y]) => [x / w, y / h]), detection: f.score,
        confidence: margin > 1 ? "high" : margin > 0.4 ? "medium" : "low", candidates: cand,
      });
    }
    const cy = (c) => c.quad.reduce((s, p) => s + p[1], 0) / 4, cx = (c) => c.quad.reduce((s, p) => s + p[0], 0) / 4;
    cards.sort((a, b) => Math.round(cy(a) * 4) - Math.round(cy(b) * 4) || cx(a) - cx(b));
    return { version: this.m.version, fallback, cards, local: true };
  }
}
